
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SignatureCanvas from 'react-signature-canvas';

interface SignatureDialogProps {
  estimateId: string;
  isOpen: boolean;
  onClose: () => void;
  onSign?: (estimateId: string, signatureData: any) => void;
}

const SignatureDialog: React.FC<SignatureDialogProps> = ({ estimateId, isOpen, onClose, onSign }) => {
  const signatureRef = useRef<SignatureCanvas>(null);
  const [signerName, setSignerName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleClear = () => {
    signatureRef.current?.clear();
  };

  const handleSign = () => {
    if (!signatureRef.current || !signerName || !termsAccepted) {
      alert('Please fill in all required fields and accept the terms');
      return;
    }

    const signatureData = {
      signature: signatureRef.current.toDataURL(),
      signerName,
      signedAt: new Date().toISOString()
    };

    onSign?.(estimateId, signatureData);
    onClose();
  };

  const termsOfService = `
TERMS OF SERVICE

1. ACCEPTANCE OF SERVICES
By signing this estimate, you agree to the terms and conditions outlined below.

2. PAYMENT TERMS
- Payment is due within 30 days of invoice date
- Late payments may incur additional fees
- Payment methods accepted: Check, ACH, Credit Card

3. SERVICE DELIVERY
- Services will be delivered as outlined in the estimate
- Any changes to the scope of work may result in additional charges
- We will provide 30 days of warranty on all services

4. LIABILITY
- Our liability is limited to the cost of services provided
- Client is responsible for data backup before service begins
- We are not liable for any indirect or consequential damages

5. CANCELLATION
- Services may be cancelled with 48 hours notice
- Cancellation fees may apply for scheduled appointments

6. CONFIDENTIALITY
- We will maintain confidentiality of your business information
- Client data will be handled according to our privacy policy

By signing below, you acknowledge that you have read, understood, and agree to these terms.
  `;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Sign Contract - Estimate #{estimateId.slice(-8)}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="max-h-64 overflow-y-auto border p-4 rounded">
            <pre className="text-sm whitespace-pre-wrap">{termsOfService}</pre>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="signerName">Full Name *</Label>
              <Input
                id="signerName"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <Label>Digital Signature *</Label>
              <div className="border rounded p-2">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'signature-canvas border rounded'
                  }}
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleClear} className="mt-2">
                Clear Signature
              </Button>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
              />
              <Label htmlFor="terms" className="text-sm">
                I accept the terms of service and authorize the work to proceed *
              </Label>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSign} disabled={!signerName || !termsAccepted}>
                Sign Contract
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignatureDialog;
